import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    Button,
    CircularProgress,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    TextField,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import BlockIcon from "@material-ui/icons/Block";
import WarningIcon from "@material-ui/icons/Warning";
import PersonIcon from "@material-ui/icons/Person";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: 24,
        backgroundColor: "#f8f9fa",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 24,
    },
    headerLeft: {},
    title: {
        fontSize: 24,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        color: "#666",
    },
    refreshButton: {
        borderRadius: 6,
        textTransform: "none",
        fontWeight: 500,
    },
    tabsContainer: {
        marginBottom: 24,
    },
    statsRow: {
        display: "flex",
        gap: 16,
        marginBottom: 24,
    },
    statCard: {
        flex: 1,
        padding: 20,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        textAlign: "center" as const,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "2px solid transparent",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
    },
    statCardActive: {
        border: "2px solid #1976d2",
        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
    },
    statValue: {
        fontSize: 32,
        fontWeight: 700,
        color: "#1a1a1a",
        lineHeight: 1,
        marginBottom: 4,
    },
    statLabel: {
        fontSize: 12,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    statFlagged: {
        backgroundColor: "#fff3e0",
        "& $statValue": { color: "#e65100" },
    },
    statRejected: {
        backgroundColor: "#ffebee",
        "& $statValue": { color: "#c62828" },
    },
    statApproved: {
        backgroundColor: "#e8f5e9",
        "& $statValue": { color: "#2e7d32" },
    },
    statRejectedReferrals: {
        backgroundColor: "#fce4ec",
        "& $statValue": { color: "#ad1457" },
    },
    tablePaper: {
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    tableHeader: {
        padding: "16px 20px",
        borderBottom: "1px solid #eee",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    tableTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    table: {
        "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: 12,
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            backgroundColor: "#f8f9fa",
            padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
            fontSize: 13,
            padding: "12px 16px",
        },
    },
    fraudChip: {
        fontSize: 10,
        fontWeight: 600,
        height: 22,
        marginRight: 4,
        marginBottom: 4,
    },
    actionButtons: {
        display: "flex",
        gap: 8,
    },
    approveButton: {
        backgroundColor: "#4caf50",
        color: "#fff",
        fontSize: 12,
        padding: "4px 12px",
        "&:hover": { backgroundColor: "#388e3c" },
    },
    processButton: {
        backgroundColor: "#1976d2",
        color: "#fff",
        fontSize: 12,
        padding: "4px 12px",
        "&:hover": { backgroundColor: "#1565c0" },
    },
    rejectButton: {
        backgroundColor: "#f44336",
        color: "#fff",
        fontSize: 12,
        padding: "4px 12px",
        "&:hover": { backgroundColor: "#d32f2f" },
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        padding: 40,
    },
    noData: {
        textAlign: "center" as const,
        padding: 60,
        color: "#999",
    },
    noDataIcon: {
        fontSize: 48,
        color: "#ddd",
        marginBottom: 16,
    },
    dialogContent: {
        minWidth: 500,
        padding: 24,
    },
    detailRow: {
        display: "flex",
        marginBottom: 12,
    },
    detailLabel: {
        width: 140,
        fontSize: 12,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    detailValue: {
        flex: 1,
        fontSize: 14,
        color: "#1a1a1a",
    },
    fraudFlags: {
        marginTop: 16,
        padding: 16,
        backgroundColor: "#ffebee",
        borderRadius: 6,
    },
    fraudFlagsTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: "#c62828",
        marginBottom: 8,
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    noteField: {
        marginTop: 16,
        "& .MuiOutlinedInput-root": {
            borderRadius: 6,
        },
    },
    successBox: {
        marginTop: 16,
        padding: 16,
        backgroundColor: "#e8f5e9",
        borderRadius: 6,
    },
    warningBox: {
        marginTop: 16,
        padding: 16,
        backgroundColor: "#fff3e0",
        borderRadius: 6,
    },
    formContainer: {
        maxWidth: 600,
        padding: 24,
    },
    formSection: {
        marginBottom: 24,
    },
    formLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    userCard: {
        padding: 16,
        backgroundColor: "#f5f5f5",
        borderRadius: 8,
        marginTop: 8,
    },
    validationResult: {
        marginTop: 16,
        padding: 16,
        borderRadius: 8,
    },
    validationSuccess: {
        backgroundColor: "#e8f5e9",
        border: "1px solid #4caf50",
    },
    validationError: {
        backgroundColor: "#ffebee",
        border: "1px solid #f44336",
    },
    errorList: {
        margin: 0,
        paddingLeft: 20,
        fontSize: 13,
        color: "#c62828",
    },
}));

const FRAUD_REVIEW_ENDPOINT = API + "/admin/referral-v2-fraud-review";

const FRAUD_FLAG_LABELS: Record<string, { label: string; color: string }> = {
    SAME_DEVICE: { label: "Same Device", color: "#c62828" },
    CIRCULAR_REFERRAL: { label: "Circular Referral", color: "#c62828" },
    CHAIN_PATTERN: { label: "Chain Pattern", color: "#e65100" },
    SUSPICIOUS_EMAIL: { label: "Suspicious Email", color: "#e65100" },
};

const ReferralFraudReview = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [activeTab, setActiveTab] = useState(0); // 0: Fraud Logs, 1: Rejected Referrals, 2: Add Manual Referral
    const [loading, setLoading] = useState(true);
    const [fraudLogs, setFraudLogs] = useState<any[]>([]);
    const [rejectedReferrals, setRejectedReferrals] = useState<any[]>([]);
    const [stats, setStats] = useState({ flagged: 0, cleared: 0, rejected: 0 });
    const [activeFilter, setActiveFilter] = useState<'flagged' | 'cleared' | 'rejected'>('flagged');

    const [selectedCommission, setSelectedCommission] = useState<any | null>(null);
    const [selectedReferral, setSelectedReferral] = useState<any | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [processDialogOpen, setProcessDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [reviewNote, setReviewNote] = useState("");
    const [processResult, setProcessResult] = useState<any | null>(null);

    // Manual referral form state
    const [referrerUserId, setReferrerUserId] = useState("");
    const [referredUserId, setReferredUserId] = useState("");
    const [manualNotes, setManualNotes] = useState("");
    const [linkOnly, setLinkOnly] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<any | null>(null);
    const [creating, setCreating] = useState(false);
    const [createResult, setCreateResult] = useState<any | null>(null);

    const loadFraudLogs = async (filter: string = activeFilter) => {
        setLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.get(`${FRAUD_REVIEW_ENDPOINT}?filter=${filter}`, config);

            if (response.data?.status) {
                setFraudLogs(response.data.data?.fraudLogs || []);
                setStats(response.data.data?.stats || { flagged: 0, cleared: 0, rejected: 0 });
            }
        } catch (error: any) {
            notify("Failed to load fraud logs", "error");
        } finally {
            setLoading(false);
        }
    };

    const loadRejectedReferrals = async () => {
        setLoading(true);
        try {
            const headers = getConfigPost();
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                { action: "get-rejected-referrals" },
                { headers }
            );

            if (response.data?.status) {
                setRejectedReferrals(response.data.data?.referrals || []);
            }
        } catch (error: any) {
            notify("Failed to load rejected referrals", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 0) {
            loadRejectedReferrals();
        } else if (activeTab === 2) {
            loadFraudLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (_event: any, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleFilterClick = (filter: 'flagged' | 'cleared' | 'rejected') => {
        setActiveFilter(filter);
        loadFraudLogs(filter);
    };

    const handleReview = (fraudLog: any) => {
        setSelectedCommission(fraudLog);
        setReviewNote("");
        setDialogOpen(true);
    };

    const handleApproveAndProcess = (referral: any) => {
        setSelectedReferral(referral);
        setReviewNote("");
        setProcessResult(null);
        setProcessDialogOpen(true);
    };

    const handleAction = async (action: "approve" | "reject") => {
        if (!selectedCommission) return;

        setActionLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.patch(
                `${FRAUD_REVIEW_ENDPOINT}/${selectedCommission.id}`,
                {
                    action,
                    notes: reviewNote || null,
                },
                config
            );

            if (response.data?.status) {
                notify(`Commission ${action === "approve" ? "approved" : "rejected"} successfully`, "success");
                setDialogOpen(false);
                loadFraudLogs();
            } else {
                notify(response.data?.message || `Failed to ${action} commission`, "error");
            }
        } catch (error: any) {
            notify(`Failed to ${action} commission`, "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleProcessReferral = async () => {
        if (!selectedReferral) return;

        setActionLoading(true);
        setProcessResult(null);

        try {
            const headers = getConfigPost();
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "approve-and-process",
                    referralId: selectedReferral.id,
                    notes: reviewNote || "Manual approval via admin panel",
                },
                { headers }
            );

            if (response.data?.status) {
                setProcessResult(response.data);
                notify("Referral approved and processed successfully!", "success");
                loadRejectedReferrals();
            } else {
                notify(response.data?.message || "Failed to process referral", "error");
            }
        } catch (error: any) {
            notify(error.response?.data?.message || "Failed to process referral", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleValidateReferral = async () => {
        if (!referrerUserId || !referredUserId) {
            notify("Please enter both referrer and referred user IDs", "warning");
            return;
        }

        setValidating(true);
        setValidationResult(null);
        setCreateResult(null);

        try {
            const headers = getConfigPost();
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "validate-referral",
                    referrerUserId,
                    referredUserId,
                    linkOnly,
                },
                { headers }
            );

            if (response.data?.status) {
                setValidationResult(response.data.data);
            } else {
                notify(response.data?.message || "Failed to validate referral", "error");
            }
        } catch (error: any) {
            notify("Failed to validate referral", "error");
        } finally {
            setValidating(false);
        }
    };

    const handleCreateManualReferral = async () => {
        if (!referrerUserId || !referredUserId) {
            notify("Please enter both referrer and referred user IDs", "warning");
            return;
        }

        setCreating(true);
        setCreateResult(null);

        try {
            const headers = getConfigPost();
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "create-manual-referral",
                    referrerUserId,
                    referredUserId,
                    notes: manualNotes || "Manual referral created via admin panel",
                    linkOnly,
                },
                { headers }
            );

            if (response.data?.status) {
                setCreateResult(response.data);
                notify(linkOnly
                    ? "Referral link created! Commission will process when user shops."
                    : "Manual referral created and processed successfully!",
                    "success"
                );
                // Reset form
                setReferrerUserId("");
                setReferredUserId("");
                setManualNotes("");
                setLinkOnly(false);
                setValidationResult(null);
            } else {
                setCreateResult(response.data);
                notify(response.data?.message || "Failed to create manual referral", "error");
            }
        } catch (error: any) {
            notify(error.response?.data?.message || "Failed to create manual referral", "error");
        } finally {
            setCreating(false);
        }
    };

    const resetManualReferralForm = () => {
        setReferrerUserId("");
        setReferredUserId("");
        setManualNotes("");
        setLinkOnly(false);
        setValidationResult(null);
        setCreateResult(null);
    };

    return (
        <div className={classes.main}>
            <div className={classes.header}>
                <div className={classes.headerLeft}>
                    <Typography className={classes.title}>Referral Review</Typography>
                    <Typography className={classes.subtitle}>
                        Review flagged fraud cases and approve rejected referrals
                    </Typography>
                </div>
                <Button
                    className={classes.refreshButton}
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => activeTab === 2 ? loadFraudLogs() : activeTab === 0 ? loadRejectedReferrals() : undefined}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </div>

            <Paper className={classes.tabsContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={`Rejected Referrals (${rejectedReferrals.length})`} />
                    <Tab label="Add Manual Referral" />
                    <Tab label="Fraud Logs" />
                </Tabs>
            </Paper>

            {activeTab === 2 && (
                <>
                    <div className={classes.statsRow}>
                        <Box
                            className={`${classes.statCard} ${classes.statFlagged} ${activeFilter === 'flagged' ? classes.statCardActive : ''}`}
                            onClick={() => handleFilterClick('flagged')}
                        >
                            <Typography className={classes.statValue}>{stats.flagged}</Typography>
                            <Typography className={classes.statLabel}>Pending Review</Typography>
                        </Box>
                        <Box
                            className={`${classes.statCard} ${classes.statApproved} ${activeFilter === 'cleared' ? classes.statCardActive : ''}`}
                            onClick={() => handleFilterClick('cleared')}
                        >
                            <Typography className={classes.statValue}>{stats.cleared}</Typography>
                            <Typography className={classes.statLabel}>Cleared</Typography>
                        </Box>
                        <Box
                            className={`${classes.statCard} ${classes.statRejected} ${activeFilter === 'rejected' ? classes.statCardActive : ''}`}
                            onClick={() => handleFilterClick('rejected')}
                        >
                            <Typography className={classes.statValue}>{stats.rejected}</Typography>
                            <Typography className={classes.statLabel}>Rejected</Typography>
                        </Box>
                    </div>

                    {loading ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : fraudLogs.length > 0 ? (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.tableHeader}>
                                <Typography className={classes.tableTitle}>
                                    {activeFilter === 'flagged' ? 'Pending Review' : activeFilter === 'cleared' ? 'Cleared' : 'Rejected'} ({fraudLogs.length})
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Referrer</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Flag Type</TableCell>
                                            <TableCell>Commission</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Created</TableCell>
                                            {activeFilter === 'flagged' && <TableCell align="center">Actions</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fraudLogs.map((log) => (
                                            <TableRow key={log.id}>
                                                <TableCell>{log.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {log.referrer_name || `#${log.referrer_user_id}`}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: "#666" }}>
                                                                {log.referrer_email}
                                                            </div>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {log.referred_name || `#${log.referred_user_id}`}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: "#666" }}>
                                                                {log.referred_email}
                                                            </div>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={log.flag_type || 'Unknown'}
                                                        className={classes.fraudChip}
                                                        style={{
                                                            backgroundColor: FRAUD_FLAG_LABELS[log.flag_type]?.color || '#666',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {log.commission_amount ? (
                                                        <span style={{ fontWeight: 600 }}>£{Number(log.commission_amount).toFixed(2)}</span>
                                                    ) : (
                                                        <span style={{ color: "#999" }}>-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={log.action_taken}
                                                        style={{
                                                            backgroundColor: log.action_taken === 'flagged' ? '#ff9800' :
                                                                log.action_taken === 'cleared' ? '#4caf50' : '#f44336',
                                                            color: '#fff',
                                                            fontSize: 10
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(log.createdAt)}</TableCell>
                                                {activeFilter === 'flagged' && (
                                                    <TableCell align="center">
                                                        <div className={classes.actionButtons}>
                                                            <Button
                                                                size="small"
                                                                className={classes.approveButton}
                                                                startIcon={<CheckIcon style={{ fontSize: 14 }} />}
                                                                onClick={() => handleReview(log)}
                                                            >
                                                                Review
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.noData}>
                                <CheckIcon className={classes.noDataIcon} />
                                <Typography variant="h6" style={{ marginBottom: 8 }}>
                                    {activeFilter === 'flagged' ? 'All Clear' : `No ${activeFilter} items`}
                                </Typography>
                                <Typography>
                                    {activeFilter === 'flagged'
                                        ? 'No flagged referrals pending review'
                                        : `No ${activeFilter} fraud logs found`}
                                </Typography>
                            </div>
                        </Paper>
                    )}
                </>
            )}

            {activeTab === 1 && (
                <Paper className={classes.tablePaper}>
                    <div className={classes.tableHeader}>
                        <Typography className={classes.tableTitle}>
                            Add Manual Referral
                        </Typography>
                        <Typography style={{ fontSize: 12, color: "#666" }}>
                            Create a referral connection between two existing users
                        </Typography>
                    </div>
                    <Box className={classes.formContainer}>
                        <div className={classes.formSection}>
                            <Typography className={classes.formLabel}>Referrer (Person who shared the code)</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                placeholder="Enter email or user ID"
                                value={referrerUserId}
                                onChange={(e) => {
                                    setReferrerUserId(e.target.value);
                                    setValidationResult(null);
                                    setCreateResult(null);
                                }}
                                size="small"
                            />
                            {validationResult?.referrerInfo && (
                                <Box className={classes.userCard}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                        <PersonIcon style={{ color: "#1976d2" }} />
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {validationResult.referrerInfo.first_name} {validationResult.referrerInfo.last_name}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#666" }}>
                                                {validationResult.referrerInfo.email}
                                            </div>
                                            {validationResult.referrerInfo.referral_code && (
                                                <code style={{ fontSize: 11, backgroundColor: "#e3f2fd", padding: "2px 6px", borderRadius: 4 }}>
                                                    Code: {validationResult.referrerInfo.referral_code}
                                                </code>
                                            )}
                                        </div>
                                    </Box>
                                </Box>
                            )}
                        </div>

                        <div className={classes.formSection}>
                            <Typography className={classes.formLabel}>Referred User (New user who will receive credit)</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                placeholder="Enter email or user ID"
                                value={referredUserId}
                                onChange={(e) => {
                                    setReferredUserId(e.target.value);
                                    setValidationResult(null);
                                    setCreateResult(null);
                                }}
                                size="small"
                            />
                            {validationResult?.referredInfo && (
                                <Box className={classes.userCard}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                        <PersonIcon style={{ color: "#4caf50" }} />
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {validationResult.referredInfo.first_name} {validationResult.referredInfo.last_name}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#666" }}>
                                                {validationResult.referredInfo.email}
                                            </div>
                                        </div>
                                    </Box>
                                </Box>
                            )}
                        </div>

                        <div className={classes.formSection}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={linkOnly}
                                        onChange={(e) => {
                                            setLinkOnly(e.target.checked);
                                            setValidationResult(null);
                                            setCreateResult(null);
                                        }}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography style={{ fontWeight: 500 }}>Link Only Mode</Typography>
                                        <Typography style={{ fontSize: 12, color: "#666" }}>
                                            Create referral link without processing commission. Commission will be processed automatically when the referred user completes their first gift card purchase.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </div>

                        {validationResult && (
                            <Box
                                className={`${classes.validationResult} ${
                                    validationResult.valid ? classes.validationSuccess : classes.validationError
                                }`}
                            >
                                {validationResult.valid ? (
                                    <>
                                        <Typography style={{ fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                            <CheckIcon style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }} />
                                            Validation Passed
                                        </Typography>
                                        <Typography style={{ fontSize: 13, color: "#666" }}>
                                            {linkOnly
                                                ? "This referral link is valid. The referred user has not used a referral code before. Commission will be processed when they complete their first purchase."
                                                : "This referral is valid. The referred user has not used a referral code before and has completed at least one gift card purchase."}
                                        </Typography>
                                        {validationResult.warnings && validationResult.warnings.length > 0 && (
                                            <Box style={{ marginTop: 8, padding: 8, backgroundColor: "#fff3e0", borderRadius: 4 }}>
                                                <Typography style={{ fontSize: 12, color: "#e65100", fontWeight: 600 }}>
                                                    Warnings (will be processed later):
                                                </Typography>
                                                <ul style={{ margin: "4px 0 0", paddingLeft: 20, fontSize: 12, color: "#e65100" }}>
                                                    {validationResult.warnings.map((w: string, idx: number) => (
                                                        <li key={idx}>{w}</li>
                                                    ))}
                                                </ul>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Typography style={{ fontWeight: 600, color: "#c62828", marginBottom: 8 }}>
                                            <WarningIcon style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }} />
                                            Validation Failed
                                        </Typography>
                                        <ul className={classes.errorList}>
                                            {validationResult.errors?.map((error: string, idx: number) => (
                                                <li key={idx}>{error}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </Box>
                        )}

                        {createResult?.status && createResult?.data && (
                            <div className={classes.successBox}>
                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                    <CheckIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                                    {createResult.data.status === "pending"
                                        ? "Referral Link Created!"
                                        : "Referral Created & Processed!"}
                                </Typography>
                                <div style={{ fontSize: 12, color: "#666" }}>
                                    <div>Referral ID: {createResult.data.referralId}</div>
                                    {createResult.data.status === "pending" ? (
                                        <div style={{ marginTop: 8, color: "#1976d2" }}>
                                            Commission will be processed automatically when the referred user completes their first gift card purchase.
                                        </div>
                                    ) : (
                                        <>
                                            {createResult.data.commissionAmount !== undefined && (
                                                <div>Commission: £{createResult.data.commissionAmount}</div>
                                            )}
                                            {createResult.data.boostWeeksAdded > 0 && (
                                                <div>Boost weeks added: {createResult.data.boostWeeksAdded}</div>
                                            )}
                                            {createResult.data.becameAmbassador && (
                                                <div style={{ color: "#1976d2", fontWeight: 600 }}>User became Ambassador!</div>
                                            )}
                                            {createResult.data.prizeDrawTicketId && (
                                                <div>Prize draw ticket issued: #{createResult.data.prizeDrawTicketId}</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {createResult && !createResult.status && createResult.validationErrors && (
                            <Box className={`${classes.validationResult} ${classes.validationError}`}>
                                <Typography style={{ fontWeight: 600, color: "#c62828", marginBottom: 8 }}>
                                    <WarningIcon style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }} />
                                    Creation Failed
                                </Typography>
                                <ul className={classes.errorList}>
                                    {createResult.validationErrors.map((error: string, idx: number) => (
                                        <li key={idx}>{error}</li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        <div className={classes.formSection}>
                            <Typography className={classes.formLabel}>Notes (Optional)</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Add any notes about this manual referral..."
                                value={manualNotes}
                                onChange={(e) => setManualNotes(e.target.value)}
                                size="small"
                            />
                        </div>

                        <Box display="flex" style={{ gap: 12 }}>
                            <Button
                                variant="outlined"
                                startIcon={validating ? <CircularProgress size={16} /> : <SearchIcon />}
                                onClick={handleValidateReferral}
                                disabled={validating || creating || !referrerUserId || !referredUserId}
                            >
                                Validate
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                onClick={handleCreateManualReferral}
                                disabled={creating || validating || !referrerUserId || !referredUserId || (validationResult && !validationResult.valid)}
                            >
                                Create Referral
                            </Button>
                            <Button
                                variant="text"
                                onClick={resetManualReferralForm}
                                disabled={creating || validating}
                            >
                                Reset
                            </Button>
                        </Box>

                        <div className={classes.warningBox} style={{ marginTop: 24 }}>
                            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#e65100", marginBottom: 8 }}>
                                Important Notes:
                            </Typography>
                            {linkOnly ? (
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#666" }}>
                                    <li>The referred user must NOT have used a referral code before</li>
                                    <li>User does NOT need to have completed a purchase yet</li>
                                    <li>Only creates the referral link - NO commission processed immediately</li>
                                    <li>Commission will be processed automatically when the user completes their first gift card purchase</li>
                                    <li>Use this for users who forgot to use their referral code at signup</li>
                                </ul>
                            ) : (
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#666" }}>
                                    <li>The referred user must NOT have used a referral code before</li>
                                    <li>The referred user must have completed at least one gift card purchase</li>
                                    <li>Creating will trigger the full commission flow (commission, boost, Sprive Plus, etc.)</li>
                                    <li>Push notifications will be sent to both users</li>
                                </ul>
                            )}
                        </div>
                    </Box>
                </Paper>
            )}

            {activeTab === 0 && (
                <>
                    {loading ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : rejectedReferrals.length > 0 ? (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.tableHeader}>
                                <Typography className={classes.tableTitle}>
                                    Rejected Referrals ({rejectedReferrals.length})
                                </Typography>
                                <Typography style={{ fontSize: 12, color: "#666" }}>
                                    These referrals were rejected due to fraud detection. You can approve and process them manually.
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Referral ID</TableCell>
                                            <TableCell>Referrer</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Fraud Flag</TableCell>
                                            <TableCell>Referral Code</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rejectedReferrals.map((referral) => (
                                            <TableRow key={referral.id}>
                                                <TableCell>{referral.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {referral.referrer_name || `#${referral.referrer_user_id}`}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: "#666" }}>
                                                                {referral.referrer_email}
                                                            </div>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {referral.referred_name || `#${referral.referred_user_id}`}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: "#666" }}>
                                                                {referral.referred_email}
                                                            </div>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {referral.fraud_flag_type ? (
                                                        <Chip
                                                            size="small"
                                                            label={referral.fraud_flag_type}
                                                            className={classes.fraudChip}
                                                            style={{
                                                                backgroundColor: FRAUD_FLAG_LABELS[referral.fraud_flag_type]?.color || '#666',
                                                                color: '#fff'
                                                            }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: "#999" }}>-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <code style={{ fontSize: 11, backgroundColor: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>
                                                        {referral.referral_code || '-'}
                                                    </code>
                                                </TableCell>
                                                <TableCell>{formatDate(referral.referral_created_at)}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        size="small"
                                                        className={classes.processButton}
                                                        startIcon={<PlayArrowIcon style={{ fontSize: 14 }} />}
                                                        onClick={() => handleApproveAndProcess(referral)}
                                                    >
                                                        Approve & Process
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.noData}>
                                <CheckIcon className={classes.noDataIcon} />
                                <Typography variant="h6" style={{ marginBottom: 8 }}>
                                    No Rejected Referrals
                                </Typography>
                                <Typography>
                                    There are no rejected referrals that need manual approval.
                                </Typography>
                            </div>
                        </Paper>
                    )}
                </>
            )}

            {/* Fraud Log Review Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ padding: "16px 24px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography style={{ fontSize: 16, fontWeight: 600 }}>Review Fraud Flag</Typography>
                        <IconButton size="small" onClick={() => setDialogOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {selectedCommission && (
                        <>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Fraud Log ID</span>
                                <span className={classes.detailValue}>{selectedCommission.id}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referral ID</span>
                                <span className={classes.detailValue}>{selectedCommission.referral_id}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referrer</span>
                                <span className={classes.detailValue}>
                                    {selectedCommission.referrer_name || `User #${selectedCommission.referrer_user_id}`} ({selectedCommission.referrer_email || 'N/A'})
                                </span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referred User</span>
                                <span className={classes.detailValue}>
                                    {selectedCommission.referred_name || `User #${selectedCommission.referred_user_id}`} ({selectedCommission.referred_email || 'N/A'})
                                </span>
                            </div>

                            <div className={classes.fraudFlags}>
                                <Typography className={classes.fraudFlagsTitle}>
                                    <WarningIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                                    Fraud Flag Details
                                </Typography>
                                <div style={{ marginBottom: 8 }}>
                                    <Chip
                                        size="small"
                                        label={selectedCommission.flag_type || 'Unknown'}
                                        style={{
                                            backgroundColor: FRAUD_FLAG_LABELS[selectedCommission.flag_type]?.color || '#666',
                                            color: '#fff'
                                        }}
                                    />
                                </div>
                                {selectedCommission.details && (
                                    <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", backgroundColor: '#fff', padding: 8, borderRadius: 4 }}>
                                        {typeof selectedCommission.details === 'string'
                                            ? selectedCommission.details
                                            : JSON.stringify(selectedCommission.details, null, 2)}
                                    </pre>
                                )}
                            </div>

                            <TextField
                                className={classes.noteField}
                                label="Review Note (optional)"
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={2}
                                value={reviewNote}
                                onChange={(e) => setReviewNote(e.target.value)}
                                placeholder="Add any notes about this review decision..."
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #eee" }}>
                    <Button
                        onClick={() => handleAction("reject")}
                        disabled={actionLoading}
                        startIcon={<BlockIcon />}
                        style={{ color: "#c62828" }}
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => handleAction("approve")}
                        disabled={actionLoading}
                        variant="contained"
                        startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                        style={{ backgroundColor: "#4caf50", color: "#fff" }}
                    >
                        Clear (Approve)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve & Process Referral Dialog */}
            <Dialog open={processDialogOpen} onClose={() => !actionLoading && setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ padding: "16px 24px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography style={{ fontSize: 16, fontWeight: 600 }}>Approve & Process Referral</Typography>
                        <IconButton size="small" onClick={() => !actionLoading && setProcessDialogOpen(false)} disabled={actionLoading}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {selectedReferral && (
                        <>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referral ID</span>
                                <span className={classes.detailValue}>{selectedReferral.id}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referrer</span>
                                <span className={classes.detailValue}>
                                    {selectedReferral.referrer_name || `User #${selectedReferral.referrer_user_id}`} ({selectedReferral.referrer_email || 'N/A'})
                                </span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referred User</span>
                                <span className={classes.detailValue}>
                                    {selectedReferral.referred_name || `User #${selectedReferral.referred_user_id}`} ({selectedReferral.referred_email || 'N/A'})
                                </span>
                            </div>

                            {selectedReferral.fraud_flag_type && (
                                <div className={classes.fraudFlags}>
                                    <Typography className={classes.fraudFlagsTitle}>
                                        <WarningIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                                        Original Fraud Flag
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={selectedReferral.fraud_flag_type}
                                        style={{
                                            backgroundColor: FRAUD_FLAG_LABELS[selectedReferral.fraud_flag_type]?.color || '#666',
                                            color: '#fff'
                                        }}
                                    />
                                    {selectedReferral.fraud_details && (
                                        <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", backgroundColor: '#fff', padding: 8, borderRadius: 4, marginTop: 8 }}>
                                            {typeof selectedReferral.fraud_details === 'string'
                                                ? selectedReferral.fraud_details
                                                : JSON.stringify(selectedReferral.fraud_details, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            )}

                            <div className={classes.warningBox}>
                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#e65100", marginBottom: 8 }}>
                                    This will process the full referral:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#666" }}>
                                    <li>Create commission for referrer (based on current campaign)</li>
                                    <li>Activate/extend boost for referrer (if applicable)</li>
                                    <li>Grant Sprive Plus to both users (if campaign allows)</li>
                                    <li>Update referrer stats (may promote to ambassador)</li>
                                    <li>Issue prize draw ticket (if prize draw is active)</li>
                                </ul>
                            </div>

                            {processResult?.status && processResult?.data && (
                                <div className={classes.successBox}>
                                    <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                        <CheckIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                                        Successfully Processed!
                                    </Typography>
                                    <div style={{ fontSize: 12, color: "#666" }}>
                                        {processResult.data.commissionAmount && (
                                            <div>Commission: £{processResult.data.commissionAmount}</div>
                                        )}
                                        {processResult.data.boostWeeksAdded > 0 && (
                                            <div>Boost weeks added: {processResult.data.boostWeeksAdded}</div>
                                        )}
                                        {processResult.data.becameAmbassador && (
                                            <div style={{ color: "#1976d2", fontWeight: 600 }}>User became Ambassador!</div>
                                        )}
                                        {processResult.data.prizeDrawTicketId && (
                                            <div>Prize draw ticket issued: #{processResult.data.prizeDrawTicketId}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!processResult && (
                                <TextField
                                    className={classes.noteField}
                                    label="Approval Note (optional)"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={reviewNote}
                                    onChange={(e) => setReviewNote(e.target.value)}
                                    placeholder="Add any notes about this approval..."
                                />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #eee" }}>
                    {processResult ? (
                        <Button
                            onClick={() => setProcessDialogOpen(false)}
                            variant="contained"
                            color="primary"
                        >
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button
                                onClick={() => setProcessDialogOpen(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleProcessReferral}
                                disabled={actionLoading}
                                variant="contained"
                                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                style={{ backgroundColor: "#1976d2", color: "#fff" }}
                            >
                                Approve & Process
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Notification />
        </div>
    );
};

export default ReferralFraudReview;
